import { useCallback, useEffect, useRef, useState } from 'react'
import {
  loadWallboard,
  refreshWallboard,
  type WallboardChart,
  type WallboardDisplayPage,
  type WallboardFigures,
  type WallboardRow,
} from '../api/wallboardDisplay'
import './WallboardPage.css'

/**
 * AFISAJUL de perete - `Monitor::wallboard($token)`, macheta
 * `version-3/monitor/wallboard.php`.
 *
 * `/adm/dashboard` raspunde la „la ce sa ma uit acum" pentru un om asezat in
 * fata lui. Asta raspunde la alta intrebare, pentru o CAMERA: „e ceva in
 * neregula chiar acum", citit de la sase metri. Cateva cifre, foarte mari, si
 * nimic de apasat.
 *
 * -----------------------------------------------------------------------------
 * FARA SESIUNE, SI DECI FARA SCHELET
 * -----------------------------------------------------------------------------
 *
 * Un televizor nu e logat. Pagina n-are antet, n-are bara de module, n-are
 * alegator de firma si nu duce NICAIERI in aplicatie - nu fiindca ar fi mai
 * curat asa, ci fiindca fiecare dintre alea ar cere o sesiune si ar trimite
 * peretele la ecranul de intrare in prima dimineata. `Shell` o sare cu totul
 * (vezi `ui/barePages.ts`), si nu se cere nimic de la niciun cap care are
 * nevoie de cookie: singurele doua cereri ale paginii merg la
 * `/api/v2/public/wallboard/...`, fara cookie deloc.
 *
 * NU E NICIO USA PE PAGINA ASTA. Nicio legatura inapoi in produs, niciun
 * export, nicio actiune, niciun formular. Singurul buton e „tot ecranul", si el
 * nu duce nicaieri si nu cere nimic - e apelul de ecran complet al browserului,
 * exact ca in portal.
 *
 * -----------------------------------------------------------------------------
 * IMPROSPATAREA
 * -----------------------------------------------------------------------------
 *
 * Pagina se redeseneaza din cifre, nu se reincarca: un perete care clipeste alb
 * la fiecare treizeci de secunde e unul pe care il stinge cineva, iar o
 * reincarcare intreaga ar pierde si tot ce a apucat browserul sa tina.
 *
 * Ce se intampla cand pica reteaua, si de ce sunt DOUA purtari:
 *
 *   n-a raspuns nimeni  - cifrele de dinainte RAMAN pe ecran, si numai batatia
 *                         inimii se face rosie dupa trei cicluri ratate. O pana
 *                         de zece secunde n-are voie sa albeasca un perete, iar
 *                         cea mai rea picare a unei table nu e sa fie gresita,
 *                         ci sa fie veche aratand exact ca una proaspata.
 *   jetonul nu mai e bun - ecranul se goleste PE LOC. Aici purtarea difera de
 *                         portal, si dinadins: acolo, un raspuns de 404 era
 *                         tratat ca orice alta picare, deci o tabla RETRASA isi
 *                         tinea ultimele cifre pe perete pana stingea cineva
 *                         televizorul. O tabla retrasa care merge mai departe
 *                         nu e o tabla retrasa.
 *
 * Cand reteaua se intoarce, nu e nimic de facut: urmatorul ciclu raspunde,
 * cifrele se inlocuiesc si inima redevine verde. Nu se stivuiesc cereri
 * ratate - e un singur cronometru, si fiecare ciclu isi anuleaza cererea
 * ramasa in aer.
 *
 * -----------------------------------------------------------------------------
 * MERGE SAPTAMANI FARA SA SE ATINGA NIMENI DE EL
 * -----------------------------------------------------------------------------
 *
 * De-aia sunt trei lucruri facute altfel decat le-ar scrie cineva din reflex:
 *
 *  - CEASUL ISI ARE COMPONENTA LUI. Bate o data pe secunda; daca ar sta in
 *    starea paginii, tot peretele s-ar redesena de 86.400 de ori pe zi degeaba.
 *    Asa, restul paginii se redeseneaza doar cand vin cifre noi.
 *  - „S-A MISCAT MOUSE-UL" NU TRECE PRIN STARE, ci pune o clasa direct pe
 *    element. Altfel fiecare pixel de miscare ar fi o redesenare.
 *  - FIECARE CRONOMETRU SI FIECARE ASCULTATOR SE STRANG la iesire, si fiecare
 *    cerere are `AbortController`-ul ei. O pagina care sta deschisa trei
 *    saptamani nu are voie sa adune nimic.
 */

/** Numele de tonuri ale serverului -> clasele foii. */
const TONES: Record<string, string> = {
  good: 'ok',
  bad: 'bad',
  warn: 'warn',
  live: 'live',
  idle: 'idle',
  plain: 'plain',
}

function tone(name: string): string {
  return TONES[name] ?? 'plain'
}

/**
 * Cel mai lat rand cu cap, si niciodata mai mult de patru.
 *
 * Jumatate din cifre pe orizontala e ce le tine aproape patrate; patru e locul
 * in care un numar inceteaza sa se mai citeasca din fundul camerei.
 */
export function columnsFor(count: number): number {
  return count > 4 ? Math.min(4, Math.ceil(count / 2)) : Math.max(1, count)
}

function two(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/**
 * Ceasul si batatia inimii.
 *
 * Componenta lui, ca bataia de o secunda sa nu redeseneze peretele - vezi capul
 * fisierului. Citeste `lastOk` printr-o referinta, deci parintele nu se
 * redeseneaza cand se schimba.
 */
function WallClock({ lastOk, every }: { lastOk: { current: number }; every: number }) {
  /* Ceasul si vechimea se citesc IN bataie, nu la desenare: ele se schimba
     singure cu timpul, iar o desenare care le citeste din nou ar da alt
     rezultat de fiecare data fara sa se fi schimbat nimic in date. */
  const [tick, setTick] = useState({ clock: '--:--:--', lit: true, stale: false })

  useEffect(() => {
    const beat = () => {
      const now = new Date()

      setTick({
        clock: `${two(now.getHours())}:${two(now.getMinutes())}:${two(now.getSeconds())}`,
        lit: now.getSeconds() % 2 === 0,
        /* O tabla careia nu i s-a raspuns de trei cicluri o spune. Cea mai rea
           picare a unui perete nu e sa fie gresit - e sa fie vechi aratand
           exact ca unul proaspat. */
        stale: Date.now() - lastOk.current > every * 3,
      })
    }

    beat()
    const id = window.setInterval(beat, 1000)

    return () => window.clearInterval(id)
  }, [every, lastOk])

  return (
    <>
      <span className="wb-clock" data-testid="wbClock">
        {tick.clock}
      </span>
      <span
        className={tick.stale ? 'wb-beat is-stale' : 'wb-beat'}
        data-testid="wbBeat"
        data-stale={tick.stale ? 'yes' : 'no'}
        style={{ opacity: tick.lit ? 1 : 0.25 }}
      />
    </>
  )
}

/**
 * Pagina pentru orice fel de jeton prost.
 *
 * Una singura, pentru toate felurile: vezi `PublicWallboardController`. Nu
 * duce la ecranul de intrare - un televizor n-are cine sa-l logheze.
 */
export function WallboardGone() {
  return (
    <div className="wbd-gone">
      <div className="g">
        <h1>This display is not available</h1>
        <p>
          The link is no longer valid, or it was never valid. Somebody with an account can issue a new one from the
          discovery dashboard.
        </p>
      </div>
    </div>
  )
}

export function WallboardPage({ token }: { token: string }) {
  const [page, setPage] = useState<WallboardDisplayPage | null>(null)
  const [figures, setFigures] = useState<WallboardFigures | null>(null)
  const [gone, setGone] = useState(false)
  /* Zero pana raspunde serverul prima data - ceasul de mai jos nu se uita la el
     inainte, fiindca nici nu e desenat. */
  const lastOk = useRef(0)
  const root = useRef<HTMLDivElement | null>(null)

  const every = (page?.refreshSeconds ?? 30) * 1000

  /* Prima desenare. Cand nu raspunde nimeni, se reincearca la ritmul tablei -
     un televizor pornit inaintea serverului trebuie sa se prinda singur din
     urma, nu sa astepte pe cineva cu o tastatura. */
  useEffect(() => {
    const abort = new AbortController()
    let timer = 0
    let stopped = false

    const attempt = async () => {
      const out = await loadWallboard(token, abort.signal)
      if (stopped) return

      if (out.kind === 'gone') {
        setGone(true)
        return
      }

      if (out.kind === 'down') {
        timer = window.setTimeout(() => void attempt(), 10000)
        return
      }

      lastOk.current = Date.now()
      setPage(out.page)
      setFigures(out.page.figures)
    }

    void attempt()

    return () => {
      stopped = true
      abort.abort()
      window.clearTimeout(timer)
    }
  }, [token])

  /* Improspatarea. Porneste abia dupa prima desenare, fiindca ritmul e al
     tablei si vine odata cu ea. */
  useEffect(() => {
    if (page === null) return

    const abort = new AbortController()

    const id = window.setInterval(() => {
      void refreshWallboard(token, abort.signal).then((out) => {
        if (out.kind === 'gone') {
          setGone(true)
          return
        }

        /* `down` nu sterge nimic: cifrele de dinainte raman, si `lastOk` nu se
           misca - de acolo se face inima rosie. */
        if (out.kind !== 'figures') return

        lastOk.current = Date.now()
        setFigures(out.figures)
      })
    }, every)

    return () => {
      window.clearInterval(id)
      abort.abort()
    }
  }, [token, page, every])

  /* Numele tablei in fila browserului, si pagina scoasa din motoarele de
     cautare: adresa ei E o parola. */
  useEffect(() => {
    if (page === null) return

    const before = document.title
    document.title = page.name

    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)

    return () => {
      document.title = before
      meta.remove()
    }
  }, [page])

  /*
   * Butonul de ecran complet se arata cand se misca indicatorul si se stinge
   * la trei secunde dupa ce s-a oprit. Clasa se pune DIRECT pe element: prin
   * stare, fiecare pixel de miscare ar fi o redesenare.
   */
  useEffect(() => {
    let asleep = 0

    const wake = () => {
      root.current?.classList.add('is-awake')
      window.clearTimeout(asleep)
      asleep = window.setTimeout(() => root.current?.classList.remove('is-awake'), 3000)
    }

    document.addEventListener('mousemove', wake)
    document.addEventListener('touchstart', wake)

    return () => {
      document.removeEventListener('mousemove', wake)
      document.removeEventListener('touchstart', wake)
      window.clearTimeout(asleep)
    }
  }, [])

  const fullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.()
      return
    }

    void document.documentElement.requestFullscreen?.()
  }, [])

  /* `F` e ce va incerca cineva stand in fata ecranului. */
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'f' || event.key === 'F') fullscreen()
    }

    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [fullscreen])

  if (gone) return <WallboardGone />

  /* Pana raspunde serverul, fundalul tablei si nimic altceva - nu un rotitor,
     care pe un perete e doar ceva ce se misca fara sa insemne nimic. */
  if (page === null || figures === null) return <div className="wbd" data-testid="wbLoading" />

  const tiles = figures.tiles

  return (
    <div className="wbd" data-theme={page.theme === 'light' ? 'light' : 'dark'} ref={root}>
      <div className="wb">
        <div className="wb-head">
          <h1 className="wb-title">{page.name}</h1>
          <span className="wb-scope">{page.viewLabel}</span>
          <WallClock lastOk={lastOk} every={every} />

          {/*
            Tot ecranul. Un afisaj de perete se deschide o data si pe urma se
            lasa acolo, iar bara browserului de-a curmezisul lui e o fasie
            permanenta de nimic.
          */}
          <button className="wb-full" onClick={fullscreen} title="Full screen" aria-label="Full screen" type="button">
            <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
            </svg>
          </button>
        </div>

        <div className="wb-tiles" data-testid="wbTiles" style={{ ['--cols' as string]: columnsFor(tiles.length) }}>
          {tiles.map((tile, i) => (
            <div className={`wb-tile ${tone(tile.tone)}`} key={`${tile.label}-${i}`}>
              <span className="wb-value">{Number(tile.value).toLocaleString()}</span>
              <span className="wb-label">{tile.label}</span>
            </div>
          ))}
        </div>

        {/*
          Banda de sub cifre e una SAU alta: paisprezece zile dintr-o masura,
          ori ce tocmai s-a intamplat. Amandoua ar injumatati inaltimea
          fiecareia pe un ecran care n-are de unde da, iar un grafic de trei
          centimetri e o podoaba, nu o citire.
        */}
        <div className="wb-list">
          {figures.chart ? <Chart chart={figures.chart} /> : <Strip title={figures.rowsTitle} rows={figures.rows} />}
        </div>
      </div>
    </div>
  )
}

function Strip({ title, rows }: { title: string; rows: WallboardRow[] }) {
  return (
    <>
      <h2>{title}</h2>
      <div data-testid="wbRows">
        {rows.length === 0 ? (
          <div className="wb-empty">Nothing yet.</div>
        ) : (
          rows.map((row, i) => (
            <div className="wb-row" key={`${row.when}-${row.what}-${i}`}>
              <span className="wb-when">{row.when.slice(11, 16)}</span>
              <span className="wb-what">{row.what}</span>
              <span className="wb-note">{row.note}</span>
              <span className={`wb-state ${tone(row.tone)}`}>{row.state}</span>
            </div>
          ))
        )}
      </div>
    </>
  )
}

/**
 * Barele, masurate fata de cea mai inalta zi din fereastra si nu fata de un
 * plafon fix - un fortnight linistit trebuie sa umple tot banda, fiindca ce se
 * citeste e FORMA, nu inaltimea absoluta.
 *
 * Se eticheteaza fiecare a doua zi. Paisprezece etichete la distanta de perete
 * se suprapun intr-o dunga cenusie; sapte se citesc curat si tot spun unde esti.
 */
function Chart({ chart }: { chart: WallboardChart }) {
  let peak = 1

  for (const series of chart.series) {
    for (const value of series.values) {
      if (value > peak) peak = value
    }
  }

  return (
    <div className="wb-chart">
      <div className="wb-chart-head">
        <h2>{chart.title}</h2>
        <div className="wb-key">
          {chart.series.map((series) => (
            <span key={series.label}>
              <i className={tone(series.tone)} />
              {series.label}
            </span>
          ))}
        </div>
      </div>
      <div className="wb-days" data-testid="wbDays">
        {chart.labels.map((label, index) => (
          <div className="wb-day" key={label + index}>
            <div className="wb-bars">
              {chart.series.map((series) => {
                const value = series.values[index] ?? 0

                return (
                  <span
                    key={series.label}
                    className={`wb-bar ${value === 0 ? 'is-zero' : tone(series.tone)}`}
                    style={{ height: `${Math.max(2, Math.round((value / peak) * 100))}%` }}
                    title={`${label}: ${value} ${series.label}`}
                  />
                )
              })}
            </div>
            <span className="wb-daylabel">{index % 2 === 0 ? label : ' '}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
